import { Link } from 'react-router-dom';
import { formatClock } from '../MentorAvailabilityPanel';
import type { MenteeMentorAvailabilitySlot } from './MenteePortalService';

const DAY_LABELS: Record<number, string> = {
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
    7: 'Sunday',
};

type Props = {
    mentorName: string | null;
    slotDurationMinutes: number;
    slots: MenteeMentorAvailabilitySlot[];
};

type DayGroup = {
    dayOfWeek: number;
    label: string;
    ranges: string[];
};

const groupSlots = (
    slots: MenteeMentorAvailabilitySlot[],
    durationMinutes: number
): DayGroup[] => {
    const byDay = new Map<number, number[]>();
    for (const slot of slots || []) {
        const day = Number(slot.dayOfWeek);
        const start = Number(slot.startMinute);
        if (!Number.isFinite(day) || !Number.isFinite(start)) continue;
        const list = byDay.get(day) || [];
        list.push(start);
        byDay.set(day, list);
    }
    return Array.from(byDay.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([dayOfWeek, starts]) => {
            const sorted = [...starts].sort((a, b) => a - b);
            return {
                dayOfWeek,
                label: DAY_LABELS[dayOfWeek] || `Day ${dayOfWeek}`,
                ranges: sorted.map((start) => {
                    const end = start + durationMinutes;
                    return `${formatClock(start)} – ${formatClock(end)} IST`;
                }),
            };
        });
};

const MenteeMyMentorAvailability = ({
    mentorName,
    slotDurationMinutes,
    slots,
}: Props) => {
    const firstName = (mentorName || 'Mentor').trim().split(/\s+/)[0] || 'Mentor';
    const groups = groupSlots(slots, slotDurationMinutes);

    return (
        <>
            <div className="card border-0 shadow-sm rounded-3">
                <div className="card-header bg-white border-bottom border-light-subtle p-3">
                    <h6 className="fw-bold mb-0 text-dark">{firstName}&apos;s Availability</h6>
                </div>
                <div className="card-body p-3">
                    {groups.length === 0 ? (
                        <p className="mmentor-empty">No availability set yet</p>
                    ) : (
                        <div className="d-flex flex-column gap-2">
                            {groups.map((group) => (
                                <div key={group.dayOfWeek} className="bg-secondary bg-opacity-10 text-dark rounded-3 p-3 d-flex flex-column gap-1">
                                    <span className="font-14 font-600">{group.label}</span>
                                    <span className="d-flex flex-wrap gap-2">
                                        {group.ranges.map((range) => (
                                            <span key={range} className="bg-white text-dark rounded-pill px-3 py-1 font-13 shadow-sm border border-light-subtle">
                                                {range}
                                            </span>
                                        ))}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mt-3">
                        {groups.length > 0 ? <p className="font-14 font-500 text-dark mb-0">{slotDurationMinutes} minutes per slot</p> : null}
                    
                    <Link to="/alumni/mentorship/mentee/sessions" className="btn-green btn-sm font-500">
                        <span>📅 View &amp; Book a Slot</span>
                    </Link>
                    </div>
                    
                </div>
            </div>
        </>
    );
};

export default MenteeMyMentorAvailability;
